import { useState, useCallback, useEffect } from 'react';
import useLocationSearch from '../hooks/search/useLocationSearch';
import {
  STUDIO_TYPE_SUGGESTIONS,
  filterActivitySuggestions,
  appendHomeSearchParams,
} from '../util/activitySearchOptions';

export const useSearchStore = () => {
  const [searchFields, setSearchFields] = useState({
    activity: '',
    location: '',
    locationPlaceId: '',
    date: '',
  });

  const [filters, setFilters] = useState({
    groupSize: '',
    creatives: [],
    bookingOptions: [],
    minPrice: 0,
    maxPrice: 300,
    activeOption: 'Alle'
  });

  const [options, setOptions] = useState({
    activity: [],
    location: [],
    date: [],
  });

  const defaultActivities = STUDIO_TYPE_SUGGESTIONS;

  const defaultLocations = [
    // { id: 'nearby', name: 'In meiner Nähe' },
    { id: 'flexible', name: 'Ich bin flexibel' },
    { id: 'Köln', name: 'Köln, Germany' },
    { id: 'Düsseldorf', name: 'Düsseldorf, Germany' },
  ];

  const phoneDefaultLocations = defaultLocations?.filter(
    (location) => location?.id !== 'flexible'
  );

  const [filteredActivities, setFilteredActivities] = useState(defaultActivities);
  const [filteredLocations, setFilteredLocations] = useState(defaultLocations);
  const [activeInput, setActiveInput] = useState(null);
  const [isPhoneSearch, setIsPhoneSearch] = useState(false);

  const getDefaultLocations = useCallback(
    () => (isPhoneSearch ? phoneDefaultLocations : defaultLocations),
    [isPhoneSearch]
  );

  // Use our new location search hook
  const locationSearch = useLocationSearch();
  
  // Sync the location search text with our searchFields
  useEffect(() => {
    if (activeInput === 'location') {
      if (searchFields?.location) {
        locationSearch?.searchLocations(searchFields?.location);
      } else {
        setFilteredLocations(getDefaultLocations());
      }
    }
  }, [searchFields?.location, activeInput, getDefaultLocations]);
  
  // When a location is selected, update the search field
  useEffect(() => {
    if (locationSearch?.selectedLocation) {
      setSearchFields(prev => ({
        ...prev,
        location: locationSearch?.selectedLocation?.name,
        locationPlaceId: locationSearch?.selectedLocation?.id || prev.locationPlaceId || ''
      }));
    }
  }, [locationSearch?.selectedLocation]);
  
  // Set filtered locations from the location search results
  useEffect(() => {
    setFilteredLocations(locationSearch?.locations);
  }, [locationSearch?.locations]);

  const updateSearchField = useCallback((field, value) => {
    setSearchFields((prevFields) => ({
      ...prevFields,
      [field]: value,
      ...(field === 'location' ? { locationPlaceId: '' } : {})
    }));

    if (field === 'activity') {
      setFilteredActivities(filterActivitySuggestions(value));
    } else if (field === 'location') {
      // If there's no search term, show default locations
      if (!value) {
        setFilteredLocations(getDefaultLocations());
      }
    }
  }, [getDefaultLocations]);

  const selectLocation = useCallback((placeId) => {
    const nextPlaceId = typeof placeId === 'string' ? placeId.trim() : '';
    setSearchFields((prevFields) => ({
      ...prevFields,
      locationPlaceId: nextPlaceId
    }));

    if (nextPlaceId) {
      locationSearch?.selectLocation(nextPlaceId);
    }
  }, [locationSearch]);

  // Add effect to show default activities when activity input is focused
  useEffect(() => {
    if (activeInput === 'activity' && !searchFields?.activity) {
      setFilteredActivities(defaultActivities);
    } else if (activeInput === 'location' && !searchFields?.location) {
      setFilteredLocations(getDefaultLocations());
    }
  }, [activeInput, getDefaultLocations]);

  // New function to handle search submission
  const handleSearchSubmit = useCallback((navigate) => {
    const searchParams = appendHomeSearchParams(new URLSearchParams(), {
      activity: searchFields.activity,
      location: searchFields.location,
      locationPlaceId: searchFields.locationPlaceId,
      date: searchFields.date,
    });

    // Add filters only if they differ from defaults
    if (filters?.groupSize) searchParams.set('groupSize', filters?.groupSize);
    if (filters?.creatives?.length) searchParams.set('creatives', filters?.creatives?.join(','));
    if (filters?.bookingOptions?.length) searchParams.set('bookingOptions', filters?.bookingOptions?.join(','));
    if (filters?.minPrice > 0) searchParams.set('minPrice', filters?.minPrice);
    if (filters?.maxPrice < 300) searchParams.set('maxPrice', filters?.maxPrice);
    if (filters?.activeOption !== 'Alle') searchParams.set('activeOption', filters?.activeOption);

    navigate(`/search?${searchParams?.toString()}`);
  }, [searchFields, filters]);

  // New function to update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  // New function to reset filters
  const resetFilters = useCallback(() => {
    setFilters({
      groupSize: '',
      creatives: [],
      bookingOptions: [],
      minPrice: 0,
      maxPrice: 300,
      activeOption: 'Alle'
    });
  }, []);

  return {
    searchFields,
    setSearchFields,
    filters,
    updateFilters,
    resetFilters,
    handleSearchSubmit,
    options,
    setOptions,
    activeInput,
    setActiveInput,
    isPhoneSearch,
    setIsPhoneSearch,
    updateSearchField,
    selectLocation,
    filteredActivities,
    setFilteredActivities,
    filteredLocations,
    setFilteredLocations,
  };
};
